/**
 * Human label for the thing a "Results" number is counting.
 *
 * A bare count is ambiguous and, in this table, actively misleading: after the Results
 * resolution work, two rows sitting next to each other can be counting completely
 * different things — 23 website start trials, 1,532 purchases, 2.9M ThruPlays, 1.4M
 * people reached. Sorting by Results ranks them against each other regardless. Ads
 * Manager solves this by printing the result type under the number ("23 / Website
 * Start Trials"), and this is that label.
 *
 * The input is `resultActionType` from the media-metrics API, which since the
 * Meta-Results passthrough can arrive in three shapes:
 *   • Meta's own indicator, source-prefixed — `actions:link_click`,
 *     `conversions:start_trial_website`, `conversion_leads:conversion_lead`
 *   • Meta's own indicator naming a top-level column — `reach`,
 *     `video_thruplay_watched_actions`
 *   • the action type our goal→key mapping resolved, for rows that predate the
 *     passthrough — `offsite_conversion.fb_pixel_purchase`, `start_trial_website`
 *
 * All three normalise to the same label, so a table mixing fresh and older rows reads
 * consistently rather than exposing which ingestion path produced each one.
 */

/**
 * `[plural, singular]` for each result type we have observed in a live Insights
 * response. Both forms are written out rather than derived: English plurals do not
 * survive a rule (a "-es" rule turns Purchases into Purcha, an "-s" rule leaves
 * "Adds to Cart" and "People Reached" untouched), and the singular is what the Cost
 * per result column reads — "Per Website Purchase", not "Per Website Purchases".
 */
const RESULT_TYPE_LABELS: Record<string, readonly [plural: string, singular: string]> = {
    // Pixel conversions
    'offsite_conversion.fb_pixel_purchase': ['Website Purchases', 'Website Purchase'],
    'offsite_conversion.fb_pixel_lead': ['Website Leads', 'Website Lead'],
    'offsite_conversion.fb_pixel_complete_registration': ['Website Registrations', 'Website Registration'],
    'offsite_conversion.fb_pixel_add_to_cart': ['Website Adds to Cart', 'Website Add to Cart'],
    'offsite_conversion.fb_pixel_initiate_checkout': ['Website Checkouts Initiated', 'Website Checkout Initiated'],
    'offsite_conversion.fb_pixel_add_payment_info': ['Website Payment Info Adds', 'Website Payment Info Add'],
    'offsite_conversion.fb_pixel_view_content': ['Website Content Views', 'Website Content View'],
    'offsite_conversion.fb_pixel_search': ['Website Searches', 'Website Search'],
    'offsite_conversion.fb_pixel_add_to_wishlist': ['Website Wishlist Adds', 'Website Wishlist Add'],
    'offsite_conversion.fb_pixel_custom': ['Custom Conversions', 'Custom Conversion'],
    omni_purchase: ['Purchases', 'Purchase'],
    // The rest of the de-duplicated `omni_*` family. These are the CANONICAL keys the conversion-event
    // catalog emits (`EVENT_CATALOG_CANONICAL_KEYS`), so any surface driven by the catalog rather than
    // by a row's `resultActionType` reaches them — without these it falls back to title-casing the raw
    // action type and prints "Omni Complete Registration" at a user.
    omni_complete_registration: ['Registrations', 'Registration'],
    omni_add_to_cart: ['Adds to Cart', 'Add to Cart'],
    omni_add_to_wishlist: ['Wishlist Adds', 'Wishlist Add'],
    omni_initiated_checkout: ['Checkouts Initiated', 'Checkout Initiated'],
    omni_view_content: ['Content Views', 'Content View'],
    omni_search: ['Searches', 'Search'],
    purchase: ['Purchases', 'Purchase'],
    // Meta's newer standard events, reported only in the `conversions` array
    start_trial_website: ['Website Start Trials', 'Website Start Trial'],
    start_trial_total: ['Start Trials', 'Start Trial'],
    start_trial_mobile_app: ['App Start Trials', 'App Start Trial'],
    subscribe_website: ['Website Subscriptions', 'Website Subscription'],
    subscribe_total: ['Subscriptions', 'Subscription'],
    contact_website: ['Website Contacts', 'Website Contact'],
    contact_total: ['Contacts', 'Contact'],
    schedule_website: ['Website Appointments', 'Website Appointment'],
    submit_application_website: ['Website Applications', 'Website Application'],
    donate_website: ['Website Donations', 'Website Donation'],
    customize_product_website: ['Website Product Customisations', 'Website Product Customisation'],
    find_location_website: ['Website Location Finds', 'Website Location Find'],
    conversion_lead: ['Conversion Leads', 'Conversion Lead'],
    // On-Facebook / messaging
    'onsite_conversion.lead_grouped': ['On-Facebook Leads', 'On-Facebook Lead'],
    'onsite_conversion.messaging_conversation_started_7d': [
        'Messaging Conversations Started',
        'Messaging Conversation Started',
    ],
    'onsite_conversion.purchase': ['Messaging Purchases', 'Messaging Purchase'],
    click_to_call_native_call_placed: ['Calls Placed', 'Call Placed'],
    // Engagement / traffic
    link_click: ['Link Clicks', 'Link Click'],
    clicks: ['Clicks', 'Click'],
    landing_page_view: ['Landing Page Views', 'Landing Page View'],
    omni_landing_page_view: ['Landing Page Views', 'Landing Page View'],
    post_engagement: ['Post Engagements', 'Post Engagement'],
    post_interaction_gross: ['Post Engagements', 'Post Engagement'],
    like: ['Page Likes', 'Page Like'],
    // The engagement keys the conversion-event catalog also offers. Without these `fallbackLabel`
    // title-cases the raw action type, and `post` — which counts SHARES — renders as "Post" beside
    // "Post Engagements", offering a conversion column whose name says the opposite of its contents.
    post_reaction: ['Reactions', 'Reaction'],
    comment: ['Comments', 'Comment'],
    post: ['Shares', 'Share'],
    'onsite_conversion.post_save': ['Saves', 'Save'],
    rsvp: ['Event Responses', 'Event Response'],
    visit_instagram_profile: ['Instagram Profile Visits', 'Instagram Profile Visit'],
    profile_visit_view: ['Profile Visits', 'Profile Visit'],
    page_visit_view: ['Page Visits', 'Page Visit'],
    // Video — named distinctly on purpose. A 3-second play is not a ThruPlay; one live
    // account read 7.3M of the former against 5.0M of the latter on the same ad sets.
    video_view: ['3-Second Video Plays', '3-Second Video Play'],
    video_thruplay_watched_actions: ['ThruPlays', 'ThruPlay'],
    video_continuous_2_sec_watched_actions: ['2-Second Video Views', '2-Second Video View'],
    video_10_sec_watched_actions: ['10-Second Video Views', '10-Second Video View'],
    // Awareness / delivery. "People", not an event count — reach is unique people.
    reach: ['People Reached', 'Person Reached'],
    impressions: ['Impressions', 'Impression'],
    estimated_ad_recallers: ['Estimated Ad Recall Lift', 'Estimated Ad Recall Lift'],
    // App
    mobile_app_install: ['App Installs', 'App Install'],
    omni_app_install: ['App Installs', 'App Install'],
    // Google
    conversions: ['Conversions', 'Conversion'],
}

/**
 * Strip Meta's `<source>:` prefix. The source (`actions`, `conversions`,
 * `conversion_leads`) tells the INGESTION which array to read; it means nothing to a
 * reader, and keeping it would render "actions:link_click" in the UI.
 *
 * Split on the FIRST colon only — a custom conversion's id can itself contain dots
 * but the source prefix is always the leading segment.
 */
function stripSourcePrefix(resultActionType: string): string {
    const colon = resultActionType.indexOf(':')
    return colon === -1 ? resultActionType : resultActionType.slice(colon + 1)
}

/**
 * Best-effort label for a result type we have no mapping for — a custom conversion
 * addressed by id, or an action type Meta added after this list was written.
 *
 * Returns a readable approximation rather than the raw key, because the raw key is
 * what makes a correct number look like a bug. A custom conversion keyed by id
 * degrades to "Custom Conversion", not "offsite conversion custom 1017292943558296".
 * Used for BOTH forms — an unknown type has no known singular, and inventing one
 * would be a guess printed as fact.
 */
function fallbackLabel(key: string): string {
    if (key.startsWith('offsite_conversion.custom.')) return 'Custom Conversion'
    if (key.startsWith('offsite_conversion.fb_pixel_custom.')) {
        // Meta names these `…fb_pixel_custom.<the advertiser's own name>`.
        return key.slice('offsite_conversion.fb_pixel_custom.'.length) || 'Custom Conversion'
    }
    const words = key
        .replace(/^offsite_conversion\.fb_pixel_/, '')
        .replace(/^onsite_conversion\./, '')
        .replace(/^(omni|offsite|onsite_web|onsite_web_app)_/, '')
        .replace(/[._]/g, ' ')
        .trim()
    if (!words) return ''
    return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The label to print under a Results count, or '' when there is nothing to say (no
 * resolved result type — the row renders "—" anyway, and an invented label there
 * would assert a meaning the number does not have).
 */
export function resultTypeLabel(resultActionType: string | null | undefined): string {
    if (!resultActionType) return ''
    const key = stripSourcePrefix(resultActionType)
    return RESULT_TYPE_LABELS[key]?.[0] ?? fallbackLabel(key)
}

/**
 * The same label phrased for a per-unit cost, mirroring Ads Manager's "Per Website
 * Start Trial" beneath its Cost per result column.
 */
export function costPerResultTypeLabel(resultActionType: string | null | undefined): string {
    if (!resultActionType) return ''
    const key = stripSourcePrefix(resultActionType)
    const singular = RESULT_TYPE_LABELS[key]?.[1] ?? fallbackLabel(key)
    return singular ? `Per ${singular}` : ''
}

/**
 * The singular label on its own, for surfaces that put the label BESIDE the value rather than under
 * a column heading — "Website Start Trial €127.04" reads as a fact, "Per Website Start Trial
 * €127.04" reads as a fragment of a sentence.
 */
export function resultTypeSingularLabel(resultActionType: string | null | undefined): string {
    if (!resultActionType) return ''
    const key = stripSourcePrefix(resultActionType)
    return RESULT_TYPE_LABELS[key]?.[1] ?? fallbackLabel(key)
}
